import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Search, Tag as TagIcon, X, Calendar } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Seo from "@/components/Seo";

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch { return ""; }
}

export default function BlogIndex() {
  const [query, setQuery] = useState("");
  const [activeTag, setActiveTag] = useState(null);
  const [items, setItems] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);

  // Debounced fetch
  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (query.trim()) params.set("q", query.trim());
        if (activeTag) params.set("tag", activeTag);
        const r = await fetch(`${API}/api/blog/articles${params.toString() ? "?" + params : ""}`);
        const j = await r.json();
        if (!alive) return;
        setItems(j.items || []);
        setAllTags(j.all_tags || []);
      } finally {
        if (alive) setLoading(false);
      }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [query, activeTag]);

  const hasFilter = useMemo(() => !!(query.trim() || activeTag), [query, activeTag]);

  return (
    <div className="min-h-screen bg-[#f8faff] text-[#1a2332] font-body" data-testid="blog-page">
      <Seo
        title="Blog permis de conduire & code de la route | DDSBillancourt"
        description="Conseils, guides pratiques et actualités pour réussir votre code et votre permis de conduire : examen, AAC, permis accéléré, permis moto — par l'auto-école DDSBillancourt (Boulogne-Billancourt, 92)."
        path="/blog"
      />
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 md:px-8 pt-12 pb-12 md:pb-16">
        <Link to="/" className="inline-flex items-center gap-2 text-[#5b6577] hover:text-[#00a86b] text-sm font-headings font-semibold mb-8" data-testid="blog-back">
          <ArrowLeft className="w-4 h-4" /> Retour à l&apos;accueil
        </Link>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <h1 className="font-headings font-black text-4xl md:text-5xl lg:text-6xl tracking-tight">
              Le blog DDS<span className="text-[#FF8C42]">Billancourt</span>
            </h1>
            <p className="mt-3 text-[#5b6577] max-w-2xl text-base">
              Conseils, guides pratiques et actualités pour réussir votre permis de conduire à Boulogne-Billancourt et partout en France.
            </p>
          </div>
          <div className="text-sm text-[#5b6577] font-headings font-semibold">
            <span className="bg-white rounded-full px-3 py-1 border border-black/5 shadow-sm" data-testid="blog-count">
              {loading ? "…" : items.length} {items.length > 1 ? "articles" : "article"}
            </span>
          </div>
        </div>

        {/* Search bar */}
        <div className="relative mb-6">
          <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-[#5b6577]" aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un article (ex : code, AAC, 92100…)"
            className="w-full bg-white border border-black/5 shadow-sm rounded-2xl pl-12 pr-12 py-4 text-base outline-none focus:ring-2 focus:ring-[#00a86b]/30 focus:border-[#00a86b]"
            data-testid="blog-search-input"
          />
          {query && (
            <button onClick={() => setQuery("")} aria-label="Effacer la recherche"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[#5b6577] hover:text-[#1a2332]"
              data-testid="blog-search-clear">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Tag chips */}
        {allTags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10" data-testid="blog-tag-list">
            <button
              onClick={() => setActiveTag(null)}
              data-testid="blog-tag-all"
              className={`text-xs font-headings font-bold rounded-full px-3 py-1.5 transition border ${!activeTag ? "bg-[#1a2332] text-white border-[#1a2332]" : "bg-white text-[#5b6577] border-black/10 hover:border-[#1a2332]"}`}
            >
              Tous
            </button>
            {allTags.map((t) => (
              <button
                key={t}
                onClick={() => setActiveTag(activeTag === t ? null : t)}
                data-testid={`blog-tag-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`inline-flex items-center gap-1 text-xs font-headings font-bold rounded-full px-3 py-1.5 transition border ${activeTag === t ? "bg-[#00a86b] text-white border-[#00a86b]" : "bg-white text-[#5b6577] border-black/10 hover:border-[#00a86b] hover:text-[#00a86b]"}`}
              >
                <TagIcon className="w-3 h-3" /> {t}
              </button>
            ))}
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="text-[#5b6577] text-sm text-center py-20" data-testid="blog-loading">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="text-center text-[#5b6577] py-20 bg-white rounded-2xl border border-black/5" data-testid="blog-empty">
            <p className="font-headings font-bold text-lg text-[#1a2332]">Aucun article trouvé</p>
            <p className="mt-2 text-sm">
              {hasFilter ? "Essayez d'élargir votre recherche ou de désactiver les filtres." : "Le blog est en cours de publication, revenez bientôt !"}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6" data-testid="blog-grid">
            {items.map((a, i) => (
              <Link
                key={a.slug}
                to={`/blog/${a.slug}`}
                data-testid={`blog-card-${i}`}
                className="group bg-white rounded-2xl border border-black/5 shadow-sm overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all flex flex-col"
              >
                {a.cover_image_url ? (
                  <div className="aspect-[16/9] overflow-hidden bg-gradient-to-br from-[#00a86b]/5 via-[#f8faff] to-[#FF8C42]/5 flex items-center justify-center">
                    <img
                      src={a.cover_image_url}
                      alt={a.title}
                      loading="lazy"
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  </div>
                ) : (
                  <div className="aspect-[16/9] bg-gradient-to-br from-[#00a86b]/10 via-[#f8faff] to-[#FF8C42]/10" />
                )}
                <div className="p-6 flex flex-col flex-1">
                  {a.tags && a.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {a.tags.slice(0, 3).map((t) => (
                        <span key={t} className="text-[10px] font-headings font-bold rounded-full bg-[#00a86b]/10 text-[#00a86b] px-2 py-0.5 uppercase tracking-wide">
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                  <h2 className="font-headings font-black text-xl text-[#1a2332] leading-snug group-hover:text-[#00a86b] transition-colors">
                    {a.title}
                  </h2>
                  {a.excerpt && (
                    <p className="mt-2 text-sm text-[#5b6577] line-clamp-3">{a.excerpt}</p>
                  )}
                  <div className="mt-auto pt-4 flex items-center justify-between text-xs text-[#5b6577]">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {formatDate(a.published_at)}
                    </span>
                    <span className="text-[#00a86b] font-headings font-bold">Lire l&apos;article →</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
